/**
 * StepBadge (BLU-1376, handover 4.2): the round step marker in the rail (24px)
 * and on the pane (30px).
 *
 * Each state differs by FORM, not colour alone:
 * - current: filled primary-strong, white number;
 * - open: white, solid line-strong ring, number;
 * - done: filled success, a tick that draws in (`check-enter`, 250ms);
 * - skipped: white, DASHED ring, number;
 * - waiting: amber, clock icon;
 * - locked: grey, lock icon, 60% opacity.
 * The badge is decoration (`aria-hidden`); the state word lives in the pill or
 * the rail text next to it.
 */
import { Check, Clock, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StepVisualState } from '../../lib/step-state';

interface StepBadgeProps {
  number: number;
  state: StepVisualState;
  size?: 'rail' | 'pane';
  className?: string;
}

const STATE_CLASS: Record<StepVisualState, string> = {
  current: 'border border-primary-strong bg-primary-strong text-white',
  open: 'border border-line-strong bg-background text-foreground',
  done: 'border border-success bg-success text-white',
  skipped: 'border border-dashed border-line-strong bg-background text-muted-foreground',
  waiting: 'border border-amber-300 bg-amber-100 text-amber-800',
  locked: 'border border-border bg-muted text-muted-foreground opacity-60',
};

export function StepBadge({ number, state, size = 'pane', className }: StepBadgeProps) {
  const pane = size === 'pane';
  const iconClass = pane ? 'h-4 w-4' : 'h-3.5 w-3.5';

  return (
    <span
      aria-hidden="true"
      data-state={state}
      data-ring={state === 'skipped' ? 'dashed' : 'solid'}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold tabular-nums',
        'transition-colors duration-150 ease-out motion-reduce:transition-none',
        pane ? 'h-[30px] w-[30px] text-sm' : 'h-6 w-6 text-xs',
        STATE_CLASS[state],
        className,
      )}
    >
      {state === 'done' ? (
        <Check className={cn(iconClass, 'motion-safe:animate-check-enter')} strokeWidth={3} />
      ) : state === 'locked' ? (
        <Lock className={iconClass} strokeWidth={2.25} />
      ) : state === 'waiting' ? (
        <Clock className={iconClass} strokeWidth={2.25} />
      ) : (
        number
      )}
    </span>
  );
}
